import * as classNames from "classNames";
import { IValidationCondition } from "../Html/Validation/IValidationCondition";
import { IValidationRule } from "../Html/Validation/IValidationRule";
import * as React from "react";
import { Calendar } from "./Calendar";
import { DateTextBoxEditor } from "../Html/DateTextBoxEditor";
import { isSameDay, addDays } from "../Utils/Date";
import { findDOMNode } from "react-dom";
import * as $ from "jquery";
import * as ReactDOM from "react-dom";

export interface IDatePickerProps {
    className?: string;
    dateFormat?: string;
    acceptDateFormats?: string[];
    acceptDateFormatsErrorMsg?: string;
    dateFormatCalendar?: string;
    disabled?: boolean;
    endDate?: any;
    excludeDates?: IValidationCondition<Date[]>;
    filterDate?: IValidationRule<Date>;
    id?: string;
    isClearable?: boolean;
    label?: string;
    labelSRSufix?: string;
    lessThan?: IValidationCondition<Date>;
    atMost?: IValidationCondition<Date>;
    atLeast?: IValidationCondition<Date>;
    moreThan?: IValidationCondition<Date>;
    onChange: (date: Date) => void;
    onFocus?: (event: React.SyntheticEvent) => void;
    placeholderText?: string;
    readOnly?: boolean;
    required?: string;
    selected?: Date;
    showYearDropdown?: boolean;
    startDate?: any; // ???
    todayButton?: string;
    labelGridClasses?: string;
    inputGridClasses?: string;
    formGroupCssWrapper?: string;
    appendErrors?: string[];
    alwaysValidateOnUpdate?: boolean;
    isNotForm?: boolean;
    width?: string;
    defaultValue?: Date;
    cssWrapper?: string;
}

export interface IDatePickerState {
    open: boolean;
}

export class DatePicker extends React.Component<IDatePickerProps, IDatePickerState> {
    private _calendarRef: Calendar;
    private _inputRef: DateTextBoxEditor;
    private _isCalendarClosing: boolean;

    constructor(props: IDatePickerProps) {
        super(props);
        this.state = { open: false };
        this._isCalendarClosing = false;
    }

    public render(): JSX.Element {
        const cssWrapper = classNames("datepicker-container", this.props.cssWrapper);

        return (
            <div className={cssWrapper} onClick={(event) => this.onIconClick(event)}>
                {this.renderDateInput()}
                <div aria-hidden="true">{this.renderClearButton()}</div>
                <div className="row" aria-hidden="true">
                    <div className={this.props.inputGridClasses ? this.props.inputGridClasses : "col-12"}>
                        <div className="datepicker-dropdown-container">{this.renderCalendar()}</div>
                    </div>
                </div>
            </div>
        );
    }

    private onIconClick(event) {
        if (!"BUTTON".includes(event.target.tagName)) {this.setOpen(true);}
    }

    private setOpen(open: boolean): void {
        // Do not dispaly Calendar selector for disabled DatePicker
        if (this.isDisabled()) {
            return;
        }
        this._isCalendarClosing = this.state.open && !open;
        this.setState({ open });
    }

    private isDisabled() {
        // About DatePicker's editability status (enabled/disabled): `disabled` of its own props and `disabled` of its child input's state may not be the same.
        // Use both of them to determine if DatePicker is disabled or not.
        const disabled = this.props.disabled || (this._inputRef && this._inputRef.isDisabled());
        return disabled;
    }

    private handleFocus(event: React.SyntheticEvent): void {
        if (this.props.onFocus) {
            this.props.onFocus(event);
        }
        this.setOpen(true);
    }

    private handleCalendarClickOutside(event: any): void {
        // would be better without jquery
        if (!$(event.target).closest($(ReactDOM.findDOMNode(this._inputRef)).find("input[type=text]")).length) {
            this.setOpen(false);
        }
    }

    private handleSelect(date: Date): void {
        this.setSelected(date);
        this.setOpen(false);
    }

    private setSelected(date: Date): void {
        if (!isSameDay(this.props.selected, date) && date != null) {
            this.props.onChange(date);
        }

        if (date == null) {
            this.props.onChange(this.props.defaultValue || null);
        }
    }

    private onInputClick(e: any): void {
        this.setOpen(true);
    }

    private onInputKeyDown(event: any): void {
        if (event.key === "Enter" || event.key === "Escape") {
            event.preventDefault();
            this.setOpen(false);
        } else if (event.key === "Tab") {
            this.setOpen(false);
        }
    }

    private onClearClick(event: any): void {
        event.preventDefault();
        this.props.onChange(null);
    }

    private renderCalendar(): JSX.Element {
        if (!this.state.open || this.isDisabled()) {
            return null;
        }

        const maxDate = this.props.atMost
            ? this.props.atMost.value
            : this.props.lessThan
            ? addDays(this.props.lessThan.value, -1)
            : null;

        const minDate = this.props.atLeast
            ? this.props.atLeast.value
            : this.props.moreThan
            ? addDays(this.props.moreThan.value, 1)
            : null;

        const excludeDates = this.props.excludeDates ? this.props.excludeDates.value : null;
        const filterDate = this.props.filterDate ? this.props.filterDate.validator : null;

        return (
            <Calendar
                ref={(ref: Calendar) => (this._calendarRef = ref)}
                dateFormat={this.props.dateFormatCalendar}
                selected={this.props.selected}
                onSelect={(e: any) => this.handleSelect(e)}
                minDate={minDate}
                maxDate={maxDate}
                startDate={this.props.startDate}
                endDate={this.props.endDate}
                excludeDates={excludeDates}
                filterDate={filterDate}
                onClickOutside={(e: any) => this.handleCalendarClickOutside(e)}
                showYearDropdown={this.props.showYearDropdown}
                todayButton={this.props.todayButton}
            />
        );
    }

    private renderDateInput(): JSX.Element {
        const className = classNames(this.props.className, { "ignore-react-onclickoutside": this.state.open } as any);
        const validationRules: IValidationRule<Date>[] = [];

        if (this.props.filterDate) {
            validationRules.push(this.props.filterDate);
        }

        if (this.props.excludeDates) {
            validationRules.push({
                validator: (date: Date) =>
                    !this.props.excludeDates.value.some((excludedDate: Date) => isSameDay(excludedDate, date)),
                errorMessage: this.props.excludeDates.errorMessage,
            });
        }

        return (
            <DateTextBoxEditor
                width={this.props.width}
                label={this.props.label}
                //labelSRSufix={this.props.labelSRSufix}
                ref={(ref: DateTextBoxEditor) => (this._inputRef = ref)}
                id={this.props.id}
                value={this.props.selected}
                atLeast={this.props.atLeast}
                atMost={this.props.atMost}
                moreThan={this.props.moreThan}
                lessThan={this.props.lessThan}
                validationRules={validationRules}
                dateFormat={this.props.dateFormat}
                acceptDateFormats={this.props.acceptDateFormats}
                //acceptDateFormatsErrorMsg={this.props.acceptDateFormatsErrorMsg}
                onFocus={(e: any) => this.handleFocus(e)}
                onChange={(d: Date) => this.setSelected(d)}
                onClick={(e: any) => this.onInputClick(e)}
                onKeyDown={(e: any) => this.onInputKeyDown(e)}
                placeholder={this.props.placeholderText}
                disabled={this.props.disabled}
                cssWrapper={this.props.formGroupCssWrapper}
                cssInput={className}
                required={this.props.required}
                labelGridClasses={this.props.labelGridClasses}
                inputGridClasses={this.props.inputGridClasses}
                isTypingFinished={this._isCalendarClosing}
                appendErrors={this.props.appendErrors}
                isNotForm={this.props.isNotForm}
                alwaysValidateOnUpdate={this.props.alwaysValidateOnUpdate}
            />
        );
    }

    private renderClearButton(): JSX.Element {
        // not styled until really needed
        if (this.props.isClearable && this.props.selected != null) {
            return (
                <a
                    aria-hidden="true"
                    className="react-datepicker__close-icon"
                    href="javascript:void(0);"
                    onClick={(e: any) => this.onClearClick(e)}
                >
                    <span className="sr-only">close</span>
                </a>
            );
        } else {
            return null;
        }
    }
}
